<script setup>
import "./orderDetails";
import { useIntersectionObserver, useToggle } from "@vueuse/core";
import { debounce } from "lodash";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Skeleton from "primevue/skeleton";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import myStudentsBulkEditModal from "@/components/MyStudents/Modals/MyStudentsBulkEditModal/index.vue";
import { HelpIcon } from "@/components/shared/icons";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import { hasUsersWritePermission } from "@/composables/authorization";
import { useApiEmployeePermissions } from "@/composables/useApiEmployeePermissions";
import { useSearchClient } from "@/composables/useSearchClient";
import { formatDateString } from "@/util/formatDateString";
import { useMyStudentsAlgoliaIndex } from "./composables/useMyStudentsAlgoliaIndex";
import ExperienceCell from "./DataCells/ExperienceCell.vue";
import ParticipantStatusCell from "./DataCells/ParticipantStatusCell.vue";
import StudentViewCell from "./DataCells/StudentViewCell.vue";
import TextCell from "./DataCells/TextCell.vue";
import HeaderTooltip from "./Display/HeaderTooltip.vue";
import { myStudentsBulkEditButtonTheme, myStudentsDataTableTheme } from "./primeVueTheme";
import { studentLink } from "./utils";

const toast = useToast();

const props = defineProps({
  orders: {
    type: Array,
    default: [],
  },
  refineNext: {
    type: Function,
  },
  isLastPage: {
    type: Boolean,
    default: false,
  },
});
const route = useRoute();
const router = useRouter();

const sentinel = ref(null);
const selectedStudents = ref([]);
const isLoading = ref(true);

const [showBulkEditModal, toggleShowBulkEditModal] = useToggle(false);

const bulkEdit = () => {
  if (selectedStudents.value?.length > 0) {
    toggleShowBulkEditModal(true);
  } else {
    toast.add({
      severity: "warn",
      summary: "Bulk Edit",
      detail: "Please select at least one item to bulk edit",
      group: "br",
      life: 5000,
    });
  }
};

const { getHitsPerPage } = useSearchClient();
const { sortOrder, selectedSortField } = useMyStudentsAlgoliaIndex();

const { hasApiEmployeeStudentViewPermission } = useApiEmployeePermissions();

useIntersectionObserver(sentinel, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    refineNextWithDebounce();
  }
});

/**
 * Debounced to prevent network storm from unresolved responses from Algolia
 */
const refineNextWithDebounce = debounce(() => {
  if (!props.isLastPage) {
    props.refineNext();
  } else {
    isLoading.value = false;
  }
}, 300);

/**
 * Update date fields with Date object
 *
 * @param {StudentInfo} record
 * @returns {StudentInfo}
 */
const fixupStudentDates = (record) => {
  const student = { ...record.student };
  student.created_at = new Date(student.created_at);
  const orders = record.orders.map((order) => {
    if (!order?.core?.created_at) {
      return { ...order };
    }
    return {
      ...order,
      core: { ...order.core, created_at: new Date(order.core.created_at) },
      session: {
        ...order.session,
        academic_start_date: new Date(order.session.academic_start_date),
        academic_end_date: new Date(order.session.academic_end_date),
      },
    };
  });
  return { ...record, student, orders };
};

/**
 * Get the students grouped by order
 * @param {[StudentInfo]} orders
 * @returns {[StudentInfo]}
 */
const groupedStudents = computed(() => {
  /**
   * @type {[StudentInfo]}
   */
  isLoading.value = true;
  const students = [];
  props.orders.forEach((student_order) => {
    const order = { ...student_order.order };

    const updateStudent = students.find(
      (student) => student_order.student.participant_id === student.student.participant_id,
    );
    if (!updateStudent) {
      const { objectID, student } = student_order;
      students.push({ objectID: objectID, student: student, orders: [order] });
    } else {
      updateStudent.orders.push(order);
    }
  });

  const studentList = students.map((student) => {
    const fixedUpStudent = fixupStudentDates(student);
    return { ...fixedUpStudent };
  });
  if (!props.isLastPage) {
    studentList.push({ objectID: "skeleton" });
  }
  const hitsPerPage = getHitsPerPage();
  if (props.orders.length >= hitsPerPage && studentList.length < hitsPerPage) {
    refineNextWithDebounce();
  } else {
    isLoading.value = false;
  }
  return studentList;
});

const selectStudent = (row) => {
  const oktaId = row?.data?.student?.participant_id;
  if (!oktaId) {
    return;
  }
  router.push({ ...studentLink(route, oktaId) });
};
</script>
<template>
  <div v-if="orders.length > 0">
    <Toast position="bottom-right" group="br" />
    <Button label="BULK ACTIONS" v-if="hasUsersWritePermission" @click="bulkEdit" :pt="myStudentsBulkEditButtonTheme"/>
    <myStudentsBulkEditModal :data="selectedStudents" :modal-visible="showBulkEditModal" @hide="toggleShowBulkEditModal(false)"/>
    <data-table
      v-model:selection="selectedStudents"
      :value="groupedStudents"
      :rows="getHitsPerPage()"
      :sort-field="selectedSortField.columnField"
      :sort-order="sortOrder === 'asc' ? 1 : -1"
      :loading="isLoading"
      stripedRows
      selectionMode="multiple"
      dataKey="objectID"
      @row-click="selectStudent"
      :pt="myStudentsDataTableTheme"
    >
      <template #loading>
        <div class="bg-gray-300 w-full h-10">
          <SkeletonLoader class="rounded-md w-full h-10" />
        </div>
      </template>
      <column selectionMode="multiple" headerStyle="width: 3rem;" />

      <column field="student.first_name_legal" header="First Name">
        <template #body="{ data }">
          <skeleton v-if="data.objectID === 'skeleton'" height="1.5rem" />
          <router-link v-else class="min-h-full w-full" :to="{ ...studentLink(route, data?.student?.participant_id) }">
            {{ data.student.first_name_legal || data.student.first_name_preferred }}
          </router-link></template
        >
      </column>
      <column field="student.last_name" header="Last Name">
        <template #body="{ data }"
          ><skeleton v-if="data.objectID === 'skeleton'" height="1.5rem" /><router-link
            v-else
            class="min-h-full w-full"
            :to="{ ...studentLink(route, data?.student?.participant_id) }"
            >{{ data.student.last_name }}</router-link
          ></template
        >
      </column>
      <column header="Email">
        <template #body="{ data }"
          ><skeleton v-if="data.objectID === 'skeleton'" height="1.5rem" /><router-link
            v-else
            class="min-h-full w-full"
            :to="{ ...studentLink(route, data?.student?.participant_id) }"
            ><text-cell :value="data.student.email" customClass="text-teal-900" /></router-link
        ></template>
      </column>
      <column header="Participant Status">
        <template #body="{ data }"><participant-status-cell :data="data" /></template>
      </column>
      <column field="student.profile_id" :pt="{ headercontent: 'flex items-center justify-center' }">
        <template #header
          ><header-tooltip label="Total Experiences" tooltip-text="Number of applications started">
            <template #headerIcon><help-icon /></template
            ><template #tooltipIcon><help-icon /></template></header-tooltip
        ></template>
        <template #body="{ data }"><experience-cell :data="data" /></template>
      </column>
      <column
        v-if="hasApiEmployeeStudentViewPermission"
        header="Student View"
        :pt="{ headercontent: 'flex items-center justify-center' }"
      >
        <template #body="{ data }"><student-view-cell :data="data" /></template>
      </column>
      <column field="student.created_at" header="Last Active">
        <template #body="{ data }">
          <skeleton v-if="data.objectID === 'skeleton'" height="1.5rem" />
          <span v-else>{{ formatDateString(data.student.created_at) }}</span>
        </template>
      </column>
    </data-table>
    <div ref="sentinel" class="flex items-center justify-center w-full p-4" />
  </div>
</template>
<style lang="css" scoped>
.p-datatable-wrapper {
  overflow: visible !important;
}
</style>
